import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { IMAGES } from "@/lib/media";
import { MaskReveal, Reveal } from "@/components/motion/Reveal";
import { useAudio } from "@/context/AudioContext";
import { WaveformPlayer } from "@/components/WaveformPlayer";

const TRACKS = [
  { id: 1, title: "Watch Out For This", genre: "DaniQ Edit · Extended", len: "3:23", src: "/media/audio/watch-out-for-this.mp3" },
  { id: 2, title: "The Ultimate Experience", genre: "DJ · Drums · MC — Live Showcase", len: "0:59", src: "/media/audio/ambient.mp3" },
];

const ProducerPage = () => {
  const navigate = useNavigate();
  const ambient = useAudio();
  const ref = useRef(null);
  const [activeId, setActiveId] = useState(null);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Duck the ambient site music while a producer track is active
  useEffect(() => {
    if (activeId) ambient?.fadeTo?.(0.03, 500);
    else ambient?.fadeTo?.(0.35, 900);
  }, [activeId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <main className="bg-ink-950 min-h-screen">
      {/* Hero */}
      <section ref={ref} className="relative h-[80vh] overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img src={IMAGES.producerHero} alt="DaniQ in studio" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/50 to-ink-950/60" />
        </motion.div>
        <div className="pointer-events-none absolute bottom-0 left-1/4 h-[400px] w-[400px] rounded-full bg-violet/25 blur-[130px]" />

        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-16 flex flex-col justify-between py-10">
          <button data-testid="producer-back" onClick={() => navigate("/")} className="flex items-center gap-2 text-white/70 hover:text-magenta transition-colors w-fit mt-16">
            <ArrowLeft className="h-4 w-4" /> <span className="text-xs uppercase tracking-[0.25em]">Back to DaniQ</span>
          </button>
          <div>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="mb-4 text-xs uppercase tracking-[0.5em] text-gold">
              The Producer
            </motion.p>
            <h1 className="font-display leading-[0.8] text-white">
              <MaskReveal className="text-[16vw] lg:text-[13vw] tracking-[0.02em]">HARDHOUSE</MaskReveal>
              <MaskReveal delay={0.1} className="text-[16vw] lg:text-[13vw] tracking-[0.02em] text-magenta">& TRANCE</MaskReveal>
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="px-6 lg:px-16 max-w-[1400px] mx-auto py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-8">
            <p className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-[0.03em] text-white">
              BEYOND THE DECKS — <span className="text-stroke-magenta">DANIQ WRITES</span> THE RAW, DRIVING ENERGY OF THE UNDERGROUND.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-4 flex items-end">
            <p className="text-white/60 leading-relaxed">
              Original productions rooted in hardhouse kicks, euphoric trance leads and percussive, live-drummed textures. Studio sound built for the mainstage.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Tracks */}
      <section className="px-6 lg:px-16 max-w-[1400px] mx-auto pb-32">
        <div className="mb-8 flex items-center justify-between">
          <h2 className="font-display text-3xl tracking-[0.2em] text-white">SELECTED WORKS</h2>
          <span className="text-xs uppercase tracking-[0.25em] text-white/40">Preview</span>
        </div>
        <div>
          {TRACKS.map((t, i) => (
            <WaveformPlayer
              key={t.id}
              track={t}
              index={i}
              isActive={activeId === t.id}
              onActivate={setActiveId}
            />
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-16 glass rounded-3xl p-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-4xl tracking-[0.1em] text-white">WANT THE FULL CATALOGUE?</h3>
              <p className="mt-2 text-white/60">Promos, stems and label enquiries welcome.</p>
            </div>
            <button data-testid="producer-book-btn" onClick={() => { navigate("/"); setTimeout(() => { const el = document.getElementById("booking"); el && el.scrollIntoView({ behavior: "smooth" }); }, 400); }}
              className="rounded-full bg-magenta px-8 py-4 text-sm uppercase tracking-[0.2em] text-white glow-magenta hover:scale-[1.03] transition-transform duration-300">
              Get In Touch
            </button>
          </div>
        </Reveal>
      </section>
    </main>
  );
};

export default ProducerPage;
